import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useUIStore, useMovieStore, useEditorStore, useTimelineStore } from '../../stores';
import { useSignalR, convertMovieResponse, convertSceneResponse, useEditorKeyboardShortcuts } from '../../hooks';
import { convertLayersResponseToTimelineData } from '../../util/timeline';
import { parseStateProperties, resolveSceneObjectLayoutProps } from '../../util/sceneObjectLayout';
import { resolveAssetUrl } from '../../util/api';
import { resolveShapeFieldsFromBody } from '../../util/shapeBody';
import { resolveTextFieldsFromBody } from '../../util/textBody';
import { extractAssetPathFromBody, resolveImageFieldsFromBody } from '../../util/imageBody';
import { Toolbar } from './Toolbar/Toolbar';
import { Stage } from './Stage';
import { Timeline } from './Timeline';
import { LeftSidebar } from './LeftSidebar';
import { RightSidebar } from './RightSidebar';
import { AIChat } from './AIChat';
import { StatusBar } from './StatusBar';
import { Welcome } from './Welcome';
import { StageTools } from './StageTools';
import './Editor.css';
import './Welcome.css';
export function Editor({ currentMovie }) {
    const { bottomPanelOpen, setWelcomeOpen } = useUIStore();
    const { currentMovie: loadedMovie, setMovie, setCurrentScene, setCurrentLayer, setLibrary, addSceneObject, updateSceneObject, setCurrentMovieResponse, } = useMovieStore();
    const { setFps, aiChatVisible } = useEditorStore();
    const { loadTimelineData } = useTimelineStore();
    useEditorKeyboardShortcuts(Boolean(loadedMovie));
    // Local state for movie selection and loading
    const [loadingState, setLoadingState] = useState('Initializing...');
    const [isMovieLoading, setIsMovieLoading] = useState(false);
    const loadingSteps = ['Loading scenes...', 'Loading scene objects...', 'Loading timeline layers...', 'Loading library...'];
    const activeStepIndex = loadingSteps.indexOf(loadingState);
    // SignalR connection 
    const { status: signalRStatus, scenesOfMovie, libraryObjects, timelineLayersOfScene } = useSignalR();
    const normalizeObjectSize = (value) => typeof value === 'number' && value > 0 ? Math.round(value) : 100;
    // load Movie data 
    const loadMovie = useCallback(async (movieResponse) => {
        setIsMovieLoading(true);
        try {
            setLoadingState(`Loading "${movieResponse.name}"...`);
            // Convert and set movie in store
            const movie = convertMovieResponse(movieResponse);
            setMovie(movie);
            setFps(movieResponse.fps);
            // Load scenes
            setLoadingState('Loading scenes...');
            const scenes = await scenesOfMovie(movieResponse);
            console.log('[Editor] Scenes loaded:', scenes);
            // Update movie with scenes
            const scenesConverted = scenes.map(convertSceneResponse);
            setMovie({ ...movie, scenes: scenesConverted });
            // Load first scene's objects and timeline (use timelineLayersOfScene - includes keyFrames + objectStates)
            if (scenes.length > 0) {
                const firstScene = scenes[0];
                setCurrentScene(convertSceneResponse(firstScene));
                setLoadingState('Loading scene objects...');
                const layersResponse = await timelineLayersOfScene(firstScene);
                console.log('[Editor] Timeline layers (with objects + keyframes + tweens + objectStates) loaded:', layersResponse);
                const mapLayeredSceneObjects = (layeredObjects) => {
                    /**
                     * flatMap kullanımı şunu yapıyordu: her katmandaki sceneObjects dizisini alıp
                     * tek bir sıralı liste haline getiriyordu (her öğe: { sceneObject, layerId }).
                     * Boş sonuç genelde flatMap yüzünden değil; hub cevabında layer.sceneObjects
                     * dolu gelmediği için oluşuyordu (TimelineLayersOfScene artık bunları da dolduruyor).
                     */
                    const sceneObjectsOnLayer = (layer) => {
                        const legacy = layer;
                        return layer.sceneObjects ?? legacy.SceneObjects ?? [];
                    };
                    const sortedLayers = [...layeredObjects].sort((a, b) => a.sorter - b.sorter);
                    if (import.meta.env.DEV) {
                        const perLayer = sortedLayers.map((layer) => ({
                            layerId: layer.id,
                            name: layer.name,
                            count: sceneObjectsOnLayer(layer).length,
                        }));
                        const total = perLayer.reduce((sum, row) => sum + row.count, 0);
                        console.debug('[Editor] Scene objects per timeline layer (before map)', {
                            layerCount: sortedLayers.length,
                            totalSceneObjects: total,
                            perLayer,
                        });
                    }
                    const ordered = [];
                    for (const layer of sortedLayers) {
                        for (const sceneObject of sceneObjectsOnLayer(layer)) {
                            ordered.push({ sceneObject, layerId: layer.id });
                        }
                    }
                    return ordered.map(({ sceneObject, layerId }, index) => {
                        const layer = layeredObjects.find((item) => item.id === layerId);
                        const props = resolveSceneObjectLayoutProps(sceneObject, layer?.keyFrames);
                        const rawSourcePath = sceneObject.url ?? sceneObject.fullPath ?? extractAssetPathFromBody(sceneObject.body);
                        const fallbackState = {
                            frame: 0,
                            x: props.left ?? props.x ?? 0,
                            y: props.top ?? props.y ?? 0,
                            width: normalizeObjectSize(props.width ?? sceneObject.width),
                            height: normalizeObjectSize(props.height ?? sceneObject.height),
                            rotation: props.rotation ?? 0,
                            opacity: props.opacity ?? 1,
                        };
                        const states = (layer?.keyFrames ?? [])
                            .map((keyFrame) => {
                            const stateForObject = keyFrame.objectStates?.find((objState) => objState.sceneObjectId === sceneObject.id);
                            const parsed = parseStateProperties(stateForObject?.properties);
                            if (!parsed)
                                return null;
                            return {
                                frame: keyFrame.frameNumber,
                                x: Math.round(parsed.left ?? parsed.x ?? fallbackState.x),
                                y: Math.round(parsed.top ?? parsed.y ?? fallbackState.y),
                                width: normalizeObjectSize(parsed.width ?? fallbackState.width),
                                height: normalizeObjectSize(parsed.height ?? fallbackState.height),
                                rotation: parsed.rotation ?? fallbackState.rotation,
                                opacity: parsed.opacity ?? fallbackState.opacity,
                            };
                        })
                            .filter((state) => state !== null)
                            .sort((a, b) => a.frame - b.frame);
                        const shapeFields = resolveShapeFieldsFromBody(sceneObject.type, sceneObject.body);
                        const textFields = resolveTextFieldsFromBody(sceneObject.type, sceneObject.body, sceneObject.name);
                        const imageFields = resolveImageFieldsFromBody(sceneObject.type, sceneObject.body);
                        const { shapeType, pathData, fill, fillMode: shapeFillMode, fillGradient: shapeFillGradient, stroke, strokeWidth, strokeStyle, strokeSides, shadow: shapeShadow, glow: shapeGlow, } = shapeFields;
                        const { fontSize, textAlign } = textFields;
                        const textFill = textFields.fill;
                        const resolvedFill = sceneObject.type === 'text' ? textFill : fill;
                        const resolvedName = sceneObject.type === 'text' ? (textFields.name ?? sceneObject.name) : sceneObject.name;
                        const resolvedFillMode = sceneObject.type === 'text' ? textFields.fillMode : sceneObject.type === 'shape' ? shapeFillMode : undefined;
                        const resolvedFillGradient = sceneObject.type === 'text'
                            ? textFields.fillGradient
                            : sceneObject.type === 'shape'
                                ? shapeFillGradient
                                : undefined;
                        const resolvedShadow = sceneObject.type === 'image'
                            ? imageFields.shadow
                            : sceneObject.type === 'text'
                                ? textFields.shadow
                                : shapeShadow;
                        const resolvedGlow = sceneObject.type === 'image'
                            ? imageFields.glow
                            : sceneObject.type === 'text'
                                ? textFields.glow
                                : shapeGlow;
                        const layoutPose = {
                            x: props.left ?? props.x ?? 0,
                            y: props.top ?? props.y ?? 0,
                            width: normalizeObjectSize(props.width ?? sceneObject.width),
                            height: normalizeObjectSize(props.height ?? sceneObject.height),
                            rotation: props.rotation ?? 0,
                            opacity: props.opacity ?? 1,
                        };
                        return {
                            id: String(sceneObject.id),
                            type: sceneObject.type || 'image',
                            name: resolvedName,
                            src: rawSourcePath ? (resolveAssetUrl(rawSourcePath) || undefined) : undefined,
                            libraryObjectId: sceneObject.libraryObjectId ?? undefined,
                            parentObjectId: sceneObject.parentObjectId != null ? String(sceneObject.parentObjectId) : null,
                            isGroup: sceneObject.isGroup === true || sceneObject.type === 'group',
                            ...layoutPose,
                            zIndex: sceneObject.sorter ?? index,
                            visible: sceneObject.isVisible !== false,
                            locked: sceneObject.isLocked === true,
                            pivotPoint: sceneObject.pivotPoint?.trim() ? sceneObject.pivotPoint : 'center',
                            layerId,
                            shapeType,
                            pathData,
                            fill: resolvedFill,
                            fillMode: resolvedFillMode,
                            fillGradient: resolvedFillGradient,
                            stroke,
                            strokeWidth,
                            strokeStyle,
                            strokeSides,
                            shadow: resolvedShadow,
                            glow: resolvedGlow,
                            fontSize,
                            textAlign,
                            defaultPose: { ...layoutPose },
                            states: states.length > 0 ? states : [fallbackState],
                        };
                    });
                };
                const mappedObjects = mapLayeredSceneObjects(layersResponse);
                mappedObjects.forEach(addSceneObject);
                // Timeline already loaded from layersResponse above
                setLoadingState('Loading timeline layers...');
                const { layers, keyframes, tweens } = convertLayersResponseToTimelineData(layersResponse);
                loadTimelineData({ layers, keyframes, tweens });
                const topLayer = [...layers].sort((a, b) => a.order - b.order)[0] ?? null;
                setCurrentLayer(topLayer);
            }
            // Load library items
            setLoadingState('Loading library...');
            const library = await libraryObjects(movieResponse);
            console.log('[Editor] Library loaded:', library);
            const mappedLibrary = library.map((item) => {
                const libraryItem = item;
                const src = libraryItem.url
                    ? resolveAssetUrl(libraryItem.url)
                    : libraryItem.fullPath
                        ? resolveAssetUrl(libraryItem.fullPath)
                        : '';
                const thumbnail = libraryItem.thumbnailUrl
                    ? resolveAssetUrl(libraryItem.thumbnailUrl)
                    : undefined;
                return {
                    id: String(item.id),
                    name: item.name,
                    type: item.type,
                    src,
                    thumbnail,
                };
            });
            setLibrary(mappedLibrary);
            // Backfill scene object sources after library is loaded.
            // Some scene objects may only carry LibraryObjectId in API payload.
            const librarySrcById = new Map(mappedLibrary
                .map((item) => [Number(item.id), item.src])
                .filter(([id, src]) => Number.isFinite(id) && Boolean(src)));
            const sceneObjectsInStore = useMovieStore.getState().currentScene?.objects ?? [];
            sceneObjectsInStore.forEach((sceneObject) => {
                if (sceneObject.src)
                    return;
                if (!sceneObject.libraryObjectId)
                    return;
                const resolvedSrc = librarySrcById.get(sceneObject.libraryObjectId);
                if (!resolvedSrc)
                    return;
                updateSceneObject(sceneObject.id, { src: resolvedSrc });
            });
            setLoadingState(`Loaded "${movieResponse.name}" successfully`);
        }
        catch (err) {
            console.error('[Editor] Failed to load movie:', err);
            setLoadingState(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
        finally {
            setIsMovieLoading(false);
        }
    }, [setMovie, setFps, setCurrentScene, setCurrentLayer, addSceneObject, updateSceneObject, setLibrary, loadTimelineData, scenesOfMovie, libraryObjects, timelineLayersOfScene]);
    // Load movie when currentMovie changes and SignalR is connected
    useEffect(() => {
        if (signalRStatus === 'Connected' && currentMovie) {
            console.log('[Editor] Loading movie:', currentMovie);
            setCurrentMovieResponse(currentMovie);
            const timeoutId = setTimeout(() => {
                loadMovie(currentMovie);
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [signalRStatus, currentMovie, loadMovie, setCurrentMovieResponse]);
    // If no movie is loaded (e.g., after delete/unload), show Welcome automatically.
    useEffect(() => {
        if (!loadedMovie) {
            setWelcomeOpen(true);
        }
    }, [loadedMovie, setWelcomeOpen]);
    return (_jsxs("div", { className: "editor", children: [_jsx(Toolbar, {}), _jsxs("div", { className: "editor-main", children: [aiChatVisible && _jsx(AIChat, {}), loadedMovie && _jsx(LeftSidebar, {}), _jsxs("div", { className: "editor-center", children: [_jsxs("div", { className: "stage-container", children: [_jsx(Welcome, {}), _jsx(Stage, {}), loadedMovie && _jsx(StageTools, {})] }), loadedMovie && bottomPanelOpen && (_jsx("div", { className: "timeline-container", children: _jsx(Timeline, {}) })), _jsx(StatusBar, { message: loadingState || null, type: loadingState ? 'error' : 'success' })] }), loadedMovie && _jsx(RightSidebar, {}), isMovieLoading && (_jsx("div", { className: "movie-loading-overlay", role: "status", "aria-live": "polite", children: _jsxs("div", { className: "movie-loading-modal", children: [_jsxs("div", { className: "movie-loading-header", children: [_jsx("div", { className: "movie-loading-spinner" }), _jsx("div", { className: "movie-loading-text", children: loadingState })] }), _jsx("div", { className: "movie-loading-steps", "aria-hidden": "true", children: loadingSteps.map((step, index) => (_jsxs("div", { className: `movie-loading-step ${index <= activeStepIndex ? 'done' : ''} ${index === activeStepIndex ? 'active' : ''}`, children: [_jsx("span", { className: "movie-loading-step-dot" }), _jsx("span", { className: "movie-loading-step-label", children: step.replace('Loading ', '').replace('...', '') })] }, step))) })] }) }))] })] }));
}
